"use client";

import { useState, useCallback, useRef, useMemo } from "react";
import * as THREE from "three";
import { 
  TextToPointCloudConfig, 
  PointCloudState, 
  GenerationState, 
  TextRenderResult, 
  CrystalDimensions,
  DEFAULT_CONFIG 
} from "./types";
import { useCanvasRenderer } from "./canvas-renderer";
import { usePixelProcessor } from "./pixel-processor";
import { useGeometryBuilder } from "./geometry-builder";

// 防抖工具函数
function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

// 错误类型枚举
enum ErrorType {
  CANVAS_ERROR = 'CANVAS_ERROR',
  PROCESSING_ERROR = 'PROCESSING_ERROR',
  GENERATION_ERROR = 'GENERATION_ERROR'
}

// 用户友好的错误信息映射
function getUserFriendlyMessage(error: Error, type: ErrorType): string {
  switch (type) {
    case ErrorType.CANVAS_ERROR:
      return "文本渲染失败，请检查文本内容和字体设置";
    case ErrorType.PROCESSING_ERROR:
      return "点云处理失败，请尝试降低密度或简化文本";
    case ErrorType.GENERATION_ERROR:
      return "点云生成失败，请重试或联系技术支持";
    default:
      return error.message || "未知错误，请重试";
  }
}


/**
 * 深度合并配置对象
 */
const mergeConfigs = (
  defaultConfig: TextToPointCloudConfig,
  userConfig: Partial<TextToPointCloudConfig>,
): TextToPointCloudConfig => {
  return { ...defaultConfig, ...userConfig };
};

/**
 * 文本转点云Hook - 重构简化版
 * 
 * @param userConfig 用户自定义配置，覆盖默认参数
 * @param crystalDimensions 水晶实际尺寸信息，用于精确缩放
 * @returns 点云状态、生成状态和控制函数
 */
export function useTextToPointCloud(
  userConfig: Partial<TextToPointCloudConfig> = {},
  crystalDimensions: CrystalDimensions = {
    width: 1.5,
    height: 1.5,
    depth: 1.5,
    maxDimension: 1.5,
    scaleRatio: 1.0,
  },
): TextRenderResult & {
  generatePointCloud: () => void;
  cleanup: () => void;
} {
  const currentConfig = useMemo(
    () => mergeConfigs(DEFAULT_CONFIG, userConfig),
    [userConfig],
  );

  // 状态管理 - 合并后的状态
  const [pointCloud, setPointCloud] = useState<PointCloudState>({
    geometry: null,
    count: 0,
    canvasDataUrl: null,
  });

  const [generation, setGeneration] = useState<GenerationState>({
    isGenerating: false,
    progress: 0,
    error: null,
  });

  // 使用各个模块
  const canvasRenderer = useCanvasRenderer();
  const pixelProcessor = usePixelProcessor();
  const geometryBuilder = useGeometryBuilder();

  // 引用和缓存
  const abortControllerRef = useRef<AbortController | null>(null);
  const cacheRef = useRef<Map<string, { geometry: THREE.BufferGeometry; count: number }>>(new Map());

  // 生成配置哈希用于缓存
  const getConfigHash = useCallback((config: TextToPointCloudConfig) => {
    return JSON.stringify({
      text: config.text,
      fontFamily: config.fontFamily,
      fontSize: config.fontSize,
      fontWeight: config.fontWeight,
      canvasWidth: config.canvasWidth,
      canvasHeight: config.canvasHeight,
      pointDensity: config.pointDensity,
      pointSize: config.pointSize,
      depthScale: config.depthScale,
    });
  }, []);

  const cleanup = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    if (pointCloud.geometry) {
      geometryBuilder.disposeGeometry(pointCloud.geometry);
    }
    // 清理缓存
    cacheRef.current.forEach(({ geometry }) => {
      geometryBuilder.disposeGeometry(geometry);
    });
    cacheRef.current.clear();
    
    canvasRenderer.cleanup();
  }, [pointCloud.geometry, geometryBuilder, canvasRenderer]);

  const generatePointCloudInternal = useCallback(async () => {
    if (!currentConfig.text.trim()) {
      setGeneration((prev) => ({ ...prev, error: null }));
      return;
    }

    // 检查缓存
    const configHash = getConfigHash(currentConfig);
    const cached = cacheRef.current.get(configHash);
    if (cached) {
      setPointCloud((prev) => {
        if (prev.geometry) {
          geometryBuilder.disposeGeometry(prev.geometry);
        }
        return {
          geometry: cached.geometry,
          count: cached.count,
          canvasDataUrl: prev.canvasDataUrl,
        };
      });
      return;
    }

    // 避免在生成中重复调用
    setGeneration((prev) => {
      if (prev.isGenerating) {
        return prev;
      }
      return { ...prev, isGenerating: true, progress: 0, error: null };
    });

    try {
      // 取消之前的生成任务
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      // 1. 渲染文本到Canvas
      setGeneration((prev) => ({ ...prev, progress: 10 }));
      let imageData;
      try {
        imageData = await canvasRenderer.renderTextToCanvas(
          currentConfig,
          (dataUrl) => {
            setPointCloud((prev) => ({ ...prev, canvasDataUrl: dataUrl }));
          }
        );
      } catch (err) {
        throw { originalError: err, type: ErrorType.CANVAS_ERROR };
      }

      // 2. 将ImageData转换为点云
      setGeneration((prev) => ({ ...prev, progress: 30 }));
      let pointsArray, colorsArray;
      try {
        const result = await pixelProcessor.convertImageDataToPoints(
          imageData,
          currentConfig,
          crystalDimensions,
          abortControllerRef.current.signal,
          (progress) => {
            setGeneration((prev) => ({ ...prev, progress: 30 + (progress * 0.6) }));
          }
        );
        pointsArray = result.points;
        colorsArray = result.colors;
      } catch (err) {
        throw { originalError: err, type: ErrorType.PROCESSING_ERROR };
      }

      // 3. 创建Three.js几何体
      setGeneration((prev) => ({ ...prev, progress: 95 }));
      let geometry;
      try {
        geometry = geometryBuilder.createPointGeometry(pointsArray, colorsArray);
      } catch (err) {
        throw { originalError: err, type: ErrorType.GENERATION_ERROR };
      }

      // 缓存结果
      const count = pointsArray.length / 3;
      cacheRef.current.set(configHash, { geometry: geometry.clone(), count });

      // 清理之前的几何体
      setPointCloud((prev) => {
        if (prev.geometry) {
          geometryBuilder.disposeGeometry(prev.geometry);
        }
        return {
          geometry,
          count,
          canvasDataUrl: prev.canvasDataUrl,
        };
      });

      setGeneration((prev) => ({ ...prev, progress: 100 }));
    } catch (err: any) {
      // 只在非中断错误时显示错误信息
      if (err?.originalError instanceof Error && !err.originalError.message.includes("aborted")) {
        const userFriendlyMessage = getUserFriendlyMessage(err.originalError, err.type || ErrorType.GENERATION_ERROR);
        setGeneration((prev) => ({ ...prev, error: userFriendlyMessage }));
        console.error("Text to point cloud generation failed:", err.originalError);
      } else if (err instanceof Error && !err.message.includes("aborted")) {
        const userFriendlyMessage = getUserFriendlyMessage(err, ErrorType.GENERATION_ERROR);
        setGeneration((prev) => ({ ...prev, error: userFriendlyMessage }));
        console.error("Text to point cloud generation failed:", err);
      } else {
        setGeneration((prev) => ({ ...prev, error: null }));
      }
    } finally {
      setGeneration((prev) => ({ ...prev, isGenerating: false }));
      setTimeout(() => {
        setGeneration((prev) => ({ ...prev, progress: 0 }));
      }, 1000);
    }
  }, [
    currentConfig,
    getConfigHash,
    canvasRenderer,
    pixelProcessor,
    geometryBuilder,
    crystalDimensions,
  ]);

  // 防抖处理 - 500ms防抖避免频繁调用
  const generatePointCloud = useMemo(
    () => debounce(generatePointCloudInternal, 500),
    [generatePointCloudInternal]
  );

  return {
    pointCloud,
    generation,
    generatePointCloud,
    cleanup,
  };
}

// 重新导出类型
export type { 
  TextToPointCloudConfig, 
  PointCloudState, 
  GenerationState, 
  TextRenderResult,
  CrystalDimensions 
} from "./types";
export { DEFAULT_CONFIG } from "./types";